import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2, Users, Mail, Phone } from "lucide-react";
import { Link } from "wouter";
import { cn } from "@/lib/utils";

interface MobileBuyerCardProps {
  buyer: {
    id: string;
    name: string;
    subType?: string | null;
    industry?: string | null;
    interestLevel?: string | null;
    investmentThesis?: string | null;
    primaryContactEmail?: string | null;
    primaryContactPhone?: string | null;
  };
}

// Canonical buyer firm types — mirrors BuyerNetwork.tsx.
// TODO: extract to a shared utility once a 4th consumer needs it.
const getSubTypeBadgeStyle = (subType: string) => {
  switch (subType) {
    case "Private Equity":
      return "bg-primary text-primary-foreground";
    case "Strategic Acquirer":
      return "bg-slate-600 text-white";
    case "Independent Sponsor":
      return "bg-teal-600 text-white";
    case "Family Office":
      return "bg-purple-600 text-white";
    case "Search Fund":
      return "bg-amber-600 text-white";
    case "Other":
      return "bg-zinc-500 text-white";
    default:
      return "bg-muted text-muted-foreground";
  }
};

export function MobileBuyerCard({ buyer }: MobileBuyerCardProps) {
  const getInterestColor = (level?: string | null) => {
    switch (level) {
      case "Hot":
        return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
      case "Warm":
        return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400";
      case "Cool":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400";
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-navy-100 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5 text-navy-600" />
          </div>
          <div className="flex-1 min-w-0">
            <Link href={`/buyers/${buyer.id}`}>
              <h3 className="font-semibold text-sm truncate hover:text-primary-text cursor-pointer">
                {buyer.name}
              </h3>
            </Link>
            {buyer.industry && (
              <p className="text-xs text-muted-foreground truncate">{buyer.industry}</p>
            )}
          </div>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mb-3">
          {buyer.subType ? (
            <Badge className={cn("text-xs", getSubTypeBadgeStyle(buyer.subType))}>
              {buyer.subType}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs text-muted-foreground border-dashed">
              Unclassified
            </Badge>
          )}
          {buyer.interestLevel && (
            <Badge className={cn("text-xs", getInterestColor(buyer.interestLevel))}>
              {buyer.interestLevel}
            </Badge>
          )}
        </div>

        {/* Thesis Preview */}
        {buyer.investmentThesis && (
          <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
            {buyer.investmentThesis}
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-3 border-t">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 h-8 text-xs"
            onClick={() => buyer.primaryContactEmail && (window.location.href = `mailto:${buyer.primaryContactEmail}`)}
            disabled={!buyer.primaryContactEmail}
          >
            <Mail className="w-3.5 h-3.5 mr-1.5" />
            Email
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1 h-8 text-xs"
            onClick={() => buyer.primaryContactPhone && (window.location.href = `tel:${buyer.primaryContactPhone}`)}
            disabled={!buyer.primaryContactPhone}
          >
            <Phone className="w-3.5 h-3.5 mr-1.5" />
            Call
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function MobileBuyerCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start gap-3 mb-3">
          <Skeleton className="w-10 h-10 rounded-lg" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
        <div className="flex gap-2 mb-3">
          <Skeleton className="h-5 w-16" />
        </div>
        <div className="space-y-1 mb-3">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-3/4" />
        </div>
        <div className="flex gap-2 pt-3 border-t">
          <Skeleton className="flex-1 h-8" />
          <Skeleton className="flex-1 h-8" />
        </div>
      </CardContent>
    </Card>
  );
}
